import { concat, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { eq } from 'ember-truth-helpers';
import MultiModelSelectorRow from 'garaje/components/multi-model-selector/row';
import TruncateWithTooltip from 'garaje/components/truncate-with-tooltip';
import { reads } from 'macro-decorators';

import type { GroupOption, NameKey, SupportedModel, VirtualItem } from '../multi-model-selector';

export interface MultiModelSelectorGroupRowSignature {
  Args: {
    option: GroupOption;
    selectedItems?: VirtualItem[];
    onSelected: (items: SupportedModel[]) => void;
    onGroupSelected?: (groupOption: GroupOption) => void;
    nameKey: NameKey;
    groupModelDisplayName: string;
    isPaginated?: boolean;
  };

  Element: HTMLElement;
}

export default class MultiModelSelectorGroupRow extends Component<MultiModelSelectorGroupRowSignature> {
  inputId = 'checkbox-' + guidFor(this);

  @reads('args.option') declare option: GroupOption;
  @reads('args.selectedItems', []) declare selectedItems: SupportedModel[];

  get checkedState(): string {
    const items = 'content' in this.option.data.items ? this.option.data.items.content : this.option.data.items;
    const groupItems = items.map((l) => l);
    const { selectedItems } = this;

    if (groupItems.every((l) => selectedItems.includes(l))) {
      return 'checked';
    }

    if (groupItems.some((l) => selectedItems.includes(l))) {
      return 'indeterminate';
    }

    return '';
  }

  @action
  onGroup(): void {
    this.args.onGroupSelected?.(this.option);
    this.#syncCheckboxState();
  }

  /**
   * Clicking an "indeterminate" checkbox typically causes that checkbox to
   * become checked. Instead, we uncheck the input and deselect all group members.
   * To override the default behavior of HTML checkboxes, #syncCheckboxState
   * applies the current checkedState to the input element.
   */
  #syncCheckboxState(): void {
    const { inputId, checkedState } = this;
    const checkbox = <HTMLInputElement>document.getElementById(inputId);

    switch (checkedState) {
      case 'checked':
        checkbox.checked = true;
        checkbox.indeterminate = false;
        break;
      case 'indeterminate':
        checkbox.checked = false;
        checkbox.indeterminate = true;
        break;
      default:
        checkbox.checked = false;
        checkbox.indeterminate = false;
        break;
    }
  }

  <template>
    <li data-test-header-selector-group={{@option.data.id}} class="p-0 {{if @isPaginated 'bg-carbon-5'}}" tabindex="-1">
      <label
        class="ember-power-select-option flex items-center space-x-2"
        aria-disabled={{if @isPaginated "true" "false"}}
      >
        <input
          type="checkbox"
          id={{this.inputId}}
          class="text-base w-3 h-6 {{if @isPaginated 'opacity-0'}}"
          disabled={{@isPaginated}}
          {{! @glint-expect-error - Not an attribute that can be set directly. Check if this works }}
          indeterminate={{eq this.checkedState "indeterminate"}}
          checked={{eq this.checkedState "checked"}}
          {{on "click" this.onGroup}}
        />
        <div class="text-carbon-70 font-normal text-base truncate">
          <TruncateWithTooltip
            @value={{concat (get @option @nameKey) " (" @groupModelDisplayName ")"}}
            @length={{24}}
          />
        </div>
      </label>
    </li>

    {{#each @option.data.items key="trackingKey" as |item|}}
      <MultiModelSelectorRow
        @subLevel={{true}}
        @item={{item}}
        @onSelected={{@onSelected}}
        @selectedItems={{this.selectedItems}}
        @nameKey={{@nameKey}}
      />
    {{/each}}
  </template>
}
